import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import Heading from "sulu-admin-bundle/components/Heading";
import Table from "sulu-admin-bundle/components/Table";
import SimpleHeaderCell from "../Table/SimpleHeaderCell";
import Moment from "moment";
import styles from "./umpire-costs-page.module.scss";

import { routeRegistry } from "sulu-admin-bundle/services/Router";
import { UrlBuilder } from "../../utils/urlBuilder";

const urlBuilder = new UrlBuilder();

const COLUMNS = [
    "match_id",
    "date",
    "team",
    "overnight",
    "fixed",
    "food",
    "public_transport",
    "private_transport",
    "total_cost",
];

const { Header, Body, Row, Cell } = Table;

const COLUMN_COUNT = COLUMNS.length + 1;

const currencyFormatter = new Intl.NumberFormat("de-DE", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
});

const formatCurrency = (value) => currencyFormatter.format(Number(value) || 0);

const UmpireCostsPage = (props) => {
    const { t } = useTranslation();
    const router = props.router;
    const {
        items: data = [],
        totalCost = 0,
        leagueName,
        seasonName,
    } = props.data || {};

    const matchRoute = useMemo(
        () => routeRegistry.getAll()["app.dhbmatches_edit_form"],
        [],
    );

    const getMatchUrl = (matchId) => {
        if (!matchRoute) return null;
        return urlBuilder.generateUrl(matchRoute.path, {
            locale: router?.attributes?.locale,
            id: matchId,
        });
    };
    // Group rows by club name
    const groups = data.reduce((acc, item) => {
        const key = item.homeTeamClubName;
        if (!acc[key]) acc[key] = [];
        acc[key].push(item);
        return acc;
    }, {});

    const rows = Object.entries(groups).flatMap(([clubName, items]) => {
        const clubTotalCost = items.reduce(
            (sum, item) => sum + item.totalCost,
            0,
        );

        const clubHeaderRow = (
            <Row key={`header-${clubName}`}>
                <Cell colSpan={COLUMN_COUNT} className={styles.clubHeaderCell}>
                    {clubName}
                </Cell>
            </Row>
        );

        const dataRows = items.map((item, i) => {
            const matchUrl = getMatchUrl(item.matchId);
            return (
                <Row key={`item-${item.matchId}`}>
                    <Cell>{i + 1}</Cell>
                    <Cell>
                        {matchUrl ? (
                            <a href={matchUrl} target="_blank" rel="noreferrer">
                                {item.matchId}
                            </a>
                        ) : (
                            item.matchId
                        )}
                    </Cell>
                    <Cell>{Moment(item.matchDate).format("DD.MM.YYYY")}</Cell>
                    <Cell>{item.homeTeamName}</Cell>
                    <Cell>{formatCurrency(item.costOvernight)}</Cell>
                    <Cell>{formatCurrency(item.costFixed)}</Cell>
                    <Cell>{formatCurrency(item.costFood)}</Cell>
                    <Cell>{formatCurrency(item.costPublicTransport)}</Cell>
                    <Cell>{formatCurrency(item.costPersonalTransport)}</Cell>
                    <Cell className={styles.costCell}>
                        {formatCurrency(item.totalCost)}
                    </Cell>
                </Row>
            );
        });

        const totalPerClubRow = (
            <Row key={`total-${clubName}`}>
                <Cell
                    colSpan={COLUMN_COUNT - 1}
                    className={styles.totalLabelCell}
                >
                    {t("dhb.umpire.costs.club_total")}
                </Cell>
                <Cell className={styles.totalCostCell}>
                    {formatCurrency(clubTotalCost)}
                </Cell>
            </Row>
        );

        return [clubHeaderRow, ...dataRows, totalPerClubRow];
    });

    const totalRow = (
        <Row key={`total`}>
            <Cell colSpan={COLUMN_COUNT - 1} className={styles.totalLabelCell}>
                {t("dhb.umpire.costs.grand_total")}
            </Cell>
            <Cell className={styles.totalCostCell}>
                {formatCurrency(totalCost)}
            </Cell>
        </Row>
    );

    const allRows = [...rows, totalRow];

    return (
        <div>
            <Heading label={leagueName} description={seasonName} />
            <div className={styles.tableWrapper}>
                <Table skin="dark">
                    <Header
                        allSelected={false}
                        selectInFirstCell={false}
                        skin="dark"
                    >
                        <SimpleHeaderCell>#</SimpleHeaderCell>
                        {COLUMNS.map((col) => (
                            <SimpleHeaderCell key={col}>
                                {t(`dhb.umpire.costs.column.${col}`)}
                            </SimpleHeaderCell>
                        ))}
                    </Header>
                    <Body selectInFirstCell={false}>{allRows}</Body>
                </Table>
            </div>
        </div>
    );
};

export default UmpireCostsPage;
